<script lang="ts">
  /* eslint-disable @typescript-eslint/no-explicit-any */
  import { fade, scale } from 'svelte/transition';
  import IconGear from '$lib/app/icons/IconGear.svelte';
  import IconClose from '$lib/app/icons/IconClose.svelte';
  import IconNavDashed from '$lib/app/icons/IconNavDashed.svelte';
  import IconNavHeadingOn from '$lib/app/icons/IconNavHeadingOn.svelte';
  import IconNavHeadingOff from '$lib/app/icons/IconNavHeadingOff.svelte';
  import IconShare from '$lib/app/icons/IconShare.svelte';
  import IconCopy from '$lib/app/icons/IconCopy.svelte';
  import IconCheck from '$lib/app/icons/IconCheck.svelte';

  import { activeStateStore } from '$lib/stores/active-state-store.svelte';
  import { elementStore } from '$lib/stores/element-store.svelte';
  import { uiStore } from '$lib/stores/ui-store.svelte';
  import { derivedStore } from '$lib/stores/derived-store.svelte';
  import { URLStateManager } from '$features/url/url-state-manager';
  import { showToast } from '$features/notifications/stores/toast-store.svelte';
  import { placeholderRegistryStore } from '$features/placeholder/stores/placeholder-registry-store.svelte';
  import { findHighestVisibleElement, captureScrollAnchor, restoreScrollAnchor } from '$lib/utils/scroll-utils';

  import ToggleItem from './ToggleItem.svelte';
  import TabGroupItem from './TabGroupItem.svelte';
  import PlaceholderItem from './PlaceholderItem.svelte';
  import { copyToClipboard } from '$lib/utils/clipboard-utils';

  interface Props {
    onclose?: () => void;
    onreset?: () => void;
    onstartShare?: () => void;
  }

  let {
    onclose = () => {},
    onreset = () => {},
    onstartShare = () => {},
  }: Props = $props();

  // --- Derived State from Core ---
  const areTabNavsVisible = $derived(uiStore.isTabGroupNavHeadingVisible);
  const showTabGroups = $derived(uiStore.uiOptions.showTabGroups);
  const showReset = $derived(uiStore.uiOptions.showReset);
  const title = $derived(uiStore.uiOptions.title);
  const description = $derived(uiStore.uiOptions.description);

  // Config Items
  const toggles = $derived(derivedStore.menuToggles);
  const tabGroups = $derived(derivedStore.menuTabGroups);
  const sectionOrder = $derived(activeStateStore.configSectionOrder);

  // State Items
  let shownToggles = $derived(activeStateStore.state.shownToggles ?? []);
  let peekToggles = $derived(activeStateStore.state.peekToggles ?? []);
  let activeTabs = $derived(activeStateStore.state.tabs ?? {});

  // Placeholder Data
  let placeholderDefinitions = $derived.by(() => {
    return placeholderRegistryStore.definitions.filter((d) => {
      if (d.hiddenFromSettings) return false;
      if (d.isLocal) {
        return elementStore.detectedPlaceholders.has(d.name);
      }
      return true;
    });
  });
  let placeholderValues = $derived(activeStateStore.state.placeholders ?? {});

  // --- UI Logic ---

  let hasCustomizeContent = $derived(
    toggles.length > 0 ||
      placeholderDefinitions.length > 0 ||
      (showTabGroups && tabGroups.length > 0),
  );

  let activeTab = $state<'customize' | 'share'>('customize');

  let copySuccess = $state(false);

  // Height preservation logic
  let mainClientHeight = $state(0);
  let preservedHeight = $state(0);

  $effect(() => {
    if (!hasCustomizeContent && activeTab === 'customize') {
      activeTab = 'share';
    }
  });

  $effect(() => {
    if (activeTab === 'customize' && mainClientHeight > preservedHeight) {
      preservedHeight = mainClientHeight;
    }
  });

  let isNavHovering = $state(false);

  function handleNavHover(hovering: boolean) {
    isNavHovering = hovering;
  }

  function handleNavToggle() {
    uiStore.isTabGroupNavHeadingVisible = !areTabNavsVisible;
  }

  // --- Core Actions ---

  function handleToggleChange(detail: any) {
    const { toggleId, value } = detail;
    activeStateStore.updateToggleState(toggleId, value);
  }

  function handleTabGroupChange(detail: any) {
    const { groupId, tabId } = detail;
    const anchorEl = findHighestVisibleElement('cv-tabgroup');
    const scrollAnchor = anchorEl ? captureScrollAnchor(anchorEl) : null;

    activeStateStore.setMarkedTab(groupId, tabId);

    if (scrollAnchor) {
      restoreScrollAnchor(scrollAnchor);
    }
  }

  function handlePlaceholderChange(detail: { name: string; value: string }) {
    activeStateStore.setPlaceholder(detail.name, detail.value);
  }

  async function copyShareUrl() {
    const url = URLStateManager.generateShareableURL(
      activeStateStore.state,
      activeStateStore.config,
      {
        toggles: elementStore.detectedToggles,
        tabGroups: elementStore.detectedTabGroups,
        placeholders: elementStore.detectedPlaceholders,
      }
    );
    try {
      await copyToClipboard(url);
      showToast('Link copied to clipboard!');
      copySuccess = true;
      setTimeout(() => {
        copySuccess = false;
      }, 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      showToast('Failed to copy URL. Please copy manually.');
    }
  }

  function computeToggleState(
    id: string,
    currentShown: string[],
    currentPeek: string[],
  ): 'show' | 'hide' | 'peek' {
    if (currentShown.includes(id)) return 'show';
    if (currentPeek.includes(id)) return 'peek';
    return 'hide';
  }
</script>

<div
  class="modal-overlay"
  onclick={(e) => {
    if (e.target === e.currentTarget) onclose();
  }}
  role="presentation"
  transition:fade={{ duration: 200 }}
>
  <div
    class="modal-box cv-custom-state-modal"
    role="dialog"
    aria-modal="true"
    transition:scale={{ duration: 200, start: 0.9 }}
  >
    <header class="header">
      <div class="header-content">
        <div class="modal-icon">
          <IconGear />
        </div>
        <div class="title">{title}</div>
      </div>
      <button type="button" class="close-btn" aria-label="Close modal" onclick={onclose}>
        <IconClose />
      </button>
    </header>

    <main
      class="main"
      bind:clientHeight={mainClientHeight}
      style:min-height={activeTab === 'share' && preservedHeight > 0
        ? `${preservedHeight}px`
        : undefined}
    >
      <div class="tabs">
        {#if hasCustomizeContent}
          <button
            type="button"
            class="tab {activeTab === 'customize' ? 'active' : ''}"
            onclick={() => (activeTab = 'customize')}>Customize</button
          >
        {/if}
        <button
          type="button"
          class="tab {activeTab === 'share' ? 'active' : ''}"
          onclick={() => (activeTab = 'share')}>Share</button
        >
      </div>

      {#if activeTab === 'customize'}
        <div class="tab-content active" in:fade={{ duration: 150 }}>
          {#if description}
            <p class="description">{description}</p>
          {/if}

          {#each sectionOrder as section (section)}
            <!-- Toggles Section -->
            {#if section === 'toggles' && toggles.length > 0}
              <div class="section">
                <div class="section-heading">Toggles</div>
                <div class="toggles-container">
                  {#each toggles as toggle (toggle.toggleId)}
                    <ToggleItem
                      {toggle}
                      value={computeToggleState(toggle.toggleId, shownToggles, peekToggles)}
                      onchange={handleToggleChange}
                    />
                  {/each}
                </div>
              </div>
            {/if}

            <!-- Placeholders Section -->
            {#if section === 'placeholders' && placeholderDefinitions.length > 0}
              <div class="section">
                <div class="section-heading">Placeholders</div>
                <div class="placeholders-container">
                  {#each placeholderDefinitions as def (def.name)}
                    <PlaceholderItem
                      definition={def}
                      value={placeholderValues[def.name] ?? def.defaultValue ?? ''}
                      onchange={handlePlaceholderChange}
                    />
                  {/each}
                </div>
              </div>
            {/if}

            <!-- Tab Groups Section -->
            {#if section === 'tabGroups' && showTabGroups && tabGroups.length > 0}
              <div class="section">
                <div class="section-heading">Tab Groups</div>
                <div class="tabgroups-container">
                  <!-- Navigation Headers Toggle -->
                  <div
                    class="tabgroup-card header-card"
                    onmouseenter={() => handleNavHover(true)}
                    onmouseleave={() => handleNavHover(false)}
                    role="group"
                  >
                    <div class="tabgroup-row">
                      <div class="logo-box" id="cv-nav-icon-box">
                        <div class="nav-icon">
                          {#if isNavHovering}
                            <IconNavDashed />
                          {:else if areTabNavsVisible}
                            <IconNavHeadingOn />
                          {:else}
                            <IconNavHeadingOff />
                          {/if}
                        </div>
                      </div>
                      <div class="tabgroup-info">
                        <div class="tabgroup-title-container">
                          <p class="tabgroup-title">Show only the selected tab</p>
                        </div>
                        <p class="tabgroup-description">Hide the navigation headers</p>
                      </div>
                      <label class="toggle-switch nav-toggle">
                        <input
                          class="nav-pref-input"
                          type="checkbox"
                          checked={!areTabNavsVisible}
                          onchange={handleNavToggle}
                          aria-label="Show only the selected tab"
                        />
                        <span class="switch-bg"></span>
                        <span class="switch-knob"></span>
                      </label>
                    </div>
                  </div>

                  <!-- Tab Groups List -->
                  <div class="tab-groups-list">
                    {#each tabGroups as group (group.groupId)}
                      <TabGroupItem
                        {group}
                        activeTabId={activeTabs[group.groupId] || group.tabs[0]?.tabId}
                        onchange={handleTabGroupChange}
                      />
                    {/each}
                  </div>
                </div>
              </div>
            {/if}
          {/each}
        </div>
      {:else}
        <div class="tab-content active" in:fade={{ duration: 150 }}>
          <div class="share-content">
            <div class="share-instruction">
              Create a shareable link for your current customization, or select specific parts of
              the page to share.
            </div>

            <button type="button" class="share-action-btn primary start-share-btn" onclick={() => onstartShare()}>
              <span class="btn-icon"
                ><IconShare /></span
              >
              <span>Select elements to share</span>
            </button>

            {#if hasCustomizeContent}
              <button type="button" class="share-action-btn copy-url-btn" onclick={copyShareUrl}>
                <span class="btn-icon">
                  {#if copySuccess}
                    <IconCheck />
                  {:else}
                    <IconCopy />
                  {/if}
                </span>
                <span>
                  {#if copySuccess}
                    Copied!
                  {:else}
                    Copy Shareable URL of Settings
                  {/if}
                </span>
              </button>
            {/if}
          </div>
        </div>
      {/if}
    </main>

    <footer class="footer">
      {#if showReset}
        <button type="button" class="reset-btn" title="Reset to Default" onclick={onreset}>Reset</button>
      {:else}
        <div></div>
      {/if}

      <div class="footer-attribution">
        <span class="footer-tagline">Browser-side page customisations provided by</span>
        <a href="https://custardui.js.org" target="_blank" rel="noopener noreferrer" class="footer-link">
          custardui.js.org
        </a>
      </div>

      <button type="button" class="done-btn" onclick={onclose}>Done</button>
    </footer>
  </div>
</div>

<style>
  /* Modal Overlay & Modal Frame */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10002;
  }

  .modal-box {
    background: var(--cv-bg);
    border-radius: var(--cv-modal-radius, 0.75rem);
    box-shadow: 0 25px 50px -12px var(--cv-shadow);
    max-width: 32rem;
    width: 90vw;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid var(--cv-border);
  }

  .header-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .modal-icon {
    position: relative;
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    color: var(--cv-text);
  }

  .modal-icon :global(svg) {
    fill: currentColor;
  }

  .title {
    font-size: 1.125rem;
    font-weight: bold;
    color: var(--cv-text);
    margin: 0;
  }

  .close-btn {
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: transparent;
    border: none;
    color: var(--cv-text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .close-btn:hover {
    background: rgba(62, 132, 244, 0.1);
    color: var(--cv-primary);
  }

  .main {
    padding: 1rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    max-height: calc(80vh - 8rem);
    min-height: var(--cv-modal-min-height, 20rem);
  }

  .description {
    font-size: 0.875rem;
    color: var(--cv-text);
    margin: 0 0 1rem 0;
    line-height: 1.4;
  }

  /* Tabs */
  .tabs {
    display: flex;
    margin-bottom: 1rem;
    border-bottom: 2px solid var(--cv-border);
  }

  .tab {
    background: transparent;
    border: none;
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--cv-text-secondary);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
  }

  .tab.active {
    color: var(--cv-primary);
    border-bottom-color: var(--cv-primary);
  }

  .tab-content {
    display: none;
  }

  .tab-content.active {
    display: block;
  }

  /* Section Styling */
  .section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .section-heading {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--cv-text-secondary);
    text-transform: var(--cv-section-label-transform, uppercase);
    letter-spacing: 0.08em;
    margin: 0;
  }

  .toggles-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    overflow: hidden;
  }

  /* Tab Groups Section specific */
  .tabgroups-container {
    border-radius: 0.5rem;
  }

  /* Nav Toggle Card */
  .tabgroup-card {
    background: var(--cv-bg);
    border-bottom: 1px solid var(--cv-border);
  }

  .tabgroup-card.header-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem;
    border: 1px solid var(--cv-border);
    border-radius: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .tabgroup-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 1rem;
  }

  .logo-box {
    width: 3rem;
    height: 3rem;
    background: var(--cv-modal-icon-bg);
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .nav-icon {
    width: 2rem;
    height: 2rem;
    color: var(--cv-text);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: color 0.2s ease;
  }

  .tabgroup-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .tabgroup-title {
    font-weight: 500;
    font-size: 0.875rem;
    color: var(--cv-text);
    margin: 0 0 0 0;
  }

  .tabgroup-description {
    font-size: 0.75rem;
    color: var(--cv-text-secondary);
    margin: 0;
    line-height: 1.3;
  }

  /* Toggle Switch */
  .toggle-switch {
    position: relative;
    display: inline-flex;
    align-items: center;
    width: 44px;
    height: 24px;
    background: var(--cv-switch-bg);
    border-radius: 9999px;
    padding: 2px;
    box-sizing: border-box;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border: none;
  }

  .toggle-switch input {
    display: none;
  }

  .toggle-switch .switch-bg {
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    background: var(--cv-switch-bg);
    transition: background-color 0.2s ease;
    pointer-events: none;
  }

  .toggle-switch .switch-knob {
    position: relative;
    width: 20px;
    height: 20px;
    background: var(--cv-switch-knob);
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;
    transform: translateX(0);
  }

  .toggle-switch input:checked ~ .switch-knob {
    transform: translateX(20px);
  }

  .toggle-switch input:checked ~ .switch-bg {
    background: var(--cv-primary);
  }

  /* Tab Groups List */
  .tab-groups-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  /* Footer */
  .footer {
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--cv-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--cv-bg);
    border-bottom-left-radius: var(--cv-modal-radius, 0.75rem);
    border-bottom-right-radius: var(--cv-modal-radius, 0.75rem);
  }

  .footer-attribution {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.1rem;
    opacity: 0.5;
    transition: opacity 0.15s ease;
  }

  .footer-attribution:hover {
    opacity: 1;
  }

  .footer-tagline {
    font-size: 0.6rem;
    color: var(--cv-text-secondary);
    letter-spacing: 0.04em;
  }

  .footer-link {
    color: var(--cv-text-secondary);
    text-decoration: none;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    transition: color 0.15s ease;
  }

  .footer-link:hover {
    color: var(--cv-primary);
  }

  .reset-btn {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: transparent;
    border: none;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--cv-text-secondary);
    cursor: pointer;
    padding: 0.4rem 0.5rem;
    border-radius: 0.5rem;
    transition: all 0.2s ease;
  }

  .reset-btn:hover {
    background: var(--cv-danger-bg);
    color: var(--cv-danger);
  }

  .done-btn {
    background: var(--cv-primary);
    color: white;
    border: none;
    padding: 0.5rem 1.1rem;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08);
    transition: background-color 0.15s ease, box-shadow 0.15s ease;
  }

  .done-btn:hover {
    background: var(--cv-primary-hover);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08);
  }

  /* Share Tab Styles */
  .share-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    text-align: center;
    padding: 1rem 0;
  }

  .share-instruction {
    font-size: 0.95rem;
    color: var(--cv-text-secondary);
    margin-bottom: 0.5rem;
  }

  .share-action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    width: 100%;
    max-width: 320px;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    font-weight: 500;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid var(--cv-border);
    background: var(--cv-bg);
    color: var(--cv-text);
  }

  .share-action-btn:hover {
    border-color: var(--cv-primary);
    color: var(--cv-primary);
    background: var(--cv-bg-hover);
  }

  .share-action-btn.primary {
    background: var(--cv-primary);
    border-color: var(--cv-primary);
    color: white;
  }

  .share-action-btn.primary:hover {
    background: var(--cv-primary-hover);
    border-color: var(--cv-primary-hover);
  }

  .btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
  }

  /* Placeholder Inputs */
  .placeholders-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
</style>
